import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Battery, Loader2 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartData,
  ChartOptions,
  ChartDataset
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chart.js/auto';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetSensorReadingsQuery, useGetLiveDataQuery } from '../services/sensorDataApi';
import { formatSensorSerial } from '../utils/sensorUtils';
import { getChartOptions } from '../utils/chartConfig';
import { smoothTemperatureData, smoothBatteryData } from '../utils/chartUtils';
import 'chartjs-adapter-date-fns';
import { useGetAlarmsQuery } from '../services/alarmApi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  annotationPlugin
);

const timeRanges = [
  { label: '24 Hours', hours: 24 },
  { label: '7 Days', hours: 168 },
  { label: '30 Days', hours: 720 },
  { label: '60 Days', hours: 1440 },
  { label: 'All Data', hours: Infinity }
];

type TimeSeriesData = {
  x: Date;
  y: number;
};

export default function SensorDetailsPage() {
  const { sensorId } = useParams<{ sensorId: string }>();
  const navigate = useNavigate();
  const chartRef = useRef<ChartJS<'line', TimeSeriesData[]>>(null);
  const zoomStateRef = useRef<any>(null); // Store zoom state
  const [selectedRange, setSelectedRange] = useState(timeRanges[0]);
  const [showBattery, setShowBattery] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - selectedRange.hours * 60 * 60 * 1000),
    end: new Date()
  });
  const [isZoomed, setIsZoomed] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);
  const [isRangeChanging, setIsRangeChanging] = useState(false);
  const [showTemp1, setShowTemp1] = useState(true);
  const [showTemp2, setShowTemp2] = useState(true);
  const [showHighAlarms, setShowHighAlarms] = useState(true);
  const [showLowAlarms, setShowLowAlarms] = useState(true);
  const [alarmPoints, setAlarmPoints] = useState<any[]>([]);
  const xAxisRangeRef = useRef<{min: number, max: number} | null>(null);
  const [xRange, setXRange] = useState<{min: number|undefined, max: number|undefined}>({min: undefined, max: undefined});

  const { data: sensors } = useGetSensorListQuery();
  const { data: readings, isLoading } = useGetSensorReadingsQuery({
    sensorId: Number(sensorId),
    startDate: dateRange.start.toISOString(),
    endDate: new Date(dateRange.end.getTime() + 60 * 60 * 1000).toISOString(),
  }, {
    pollingInterval: 30000
  });
  const { data: alarms } = useGetAlarmsQuery();
  const { data: liveData } = useGetLiveDataQuery(undefined, {
    pollingInterval: 10000,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const sensor = sensors?.find(s => s.id === Number(sensorId));
  
  if (!sensor) {
    return (
      <div className="p-6">
        <div className="text-red-600">Sensor not found</div>
      </div>
    );
  }

  const alarm = alarms?.find((a: any) => a.sensor_id === sensor?.id);
  const alarmSetpoints = {
    high: (alarm as any)?.high_alarm,
    low: (alarm as any)?.low_alarm,
  };

  // Find the latest live data for this sensor
  const liveSensor = liveData?.find(ld => ld.sensor_id === sensor.id.toString());
  // Find the latest timestamp in readings
  const lastReadingTimestamp = readings && readings.length > 0 ? new Date(readings[readings.length - 1].timestamp) : null;
  // Only overlay if live data is newer
  const shouldOverlayLive = liveSensor && liveSensor.timestamp && (!lastReadingTimestamp || new Date(liveSensor.timestamp) > lastReadingTimestamp);

  // Cleanup chart instance on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    setDateRange({
      start: selectedRange.hours === Infinity 
        ? new Date(0) // Start from epoch for all data
        : new Date(Date.now() - selectedRange.hours * 60 * 60 * 1000),
      end: new Date()
    });
  }, [selectedRange]);

  // Set chart ready state when data is available
  useEffect(() => {
    if (readings && !isLoading) {
      setIsChartReady(true);
      setIsRangeChanging(false);
    }
  }, [readings, isLoading]);

  // Save zoom state before data changes
  useEffect(() => {
    if (chartRef.current && typeof (chartRef.current as any).getZoom === 'function') {
      zoomStateRef.current = (chartRef.current as any).getZoom();
    }
    // eslint-disable-next-line
  }, [readings, liveData]);

  // Restore zoom state after chart updates
  useEffect(() => {
    if (chartRef.current && zoomStateRef.current && typeof (chartRef.current as any).zoom === 'function') {
      (chartRef.current as any).zoom(zoomStateRef.current);
    }
    // eslint-disable-next-line
  }, [isChartReady, readings, liveData]);

  const moveTimeWindow = (direction: 'forward' | 'backward') => {
    if (selectedRange.hours === Infinity) return; // Don't allow moving window for all data
    
    const timeWindow = selectedRange.hours * 60 * 60 * 1000;
    setDateRange(prev => {
      const offset = direction === 'forward' ? timeWindow : -timeWindow;
      return {
        start: new Date(prev.start.getTime() + offset),
        end: new Date(prev.end.getTime() + offset)
      };
    });
  };

  const handleRangeChange = (range: typeof timeRanges[0]) => {
    setIsRangeChanging(true);
    setSelectedRange(range);
  };

  const handleResetZoom = useCallback(() => {
    setXRange({min: undefined, max: undefined});
    if (chartRef.current) {
      chartRef.current.resetZoom();
      setIsZoomed(false);
    }
  }, []);

  const sensorAlarms = alarmPoints.filter(
    alarm => alarm.sensor_id === sensor?.id
  );
  const highAlarms = sensorAlarms.filter(a => String(a.alarm_type) === '1');
  const lowAlarms = sensorAlarms.filter(a => String(a.alarm_type) === '2');

  const highAlarmDataset: ChartDataset<'scatter', TimeSeriesData[]> = {
    label: 'High Alarm',
    data: showHighAlarms ? highAlarms.map(a => ({ x: new Date(a.activated), y: a.alarm_values })) : [],
    pointBackgroundColor: 'red',
    pointBorderColor: 'red',
    pointRadius: 6,
    type: 'scatter',
    showLine: false,
    yAxisID: 'y',
  };
  const lowAlarmDataset: ChartDataset<'scatter', TimeSeriesData[]> = {
    label: 'Low Alarm',
    data: showLowAlarms ? lowAlarms.map(a => ({ x: new Date(a.activated), y: a.alarm_values })) : [],
    pointBackgroundColor: 'blue',
    pointBorderColor: 'blue',
    pointRadius: 6,
    type: 'scatter',
    showLine: false,
    yAxisID: 'y',
  };

  // Compute datasets and options for direct mutation
  const datasets = useMemo(() => [
    ...(showTemp1 ? [{
      label: 'Temperature (°C)',
      data: readings ? smoothTemperatureData(
        readings
          .filter(reading => 
            reading.temperature !== null &&
            reading.temperature !== 8001 && 
            reading.temperature !== 8002
          )
          .map(reading => ({
            x: new Date(reading.timestamp),
            y: reading.temperature as number
          }))
      ) : [],
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y'
    },
    ...(shouldOverlayLive && typeof liveSensor.temperature === 'number' && showTemp1 ? [{
      label: 'Live Temperature',
      data: [{ x: new Date(liveSensor.timestamp), y: liveSensor.temperature }],
      borderColor: 'rgba(75,192,192,0.5)',
      backgroundColor: 'rgba(75,192,192,1)',
      pointRadius: 6,
      pointStyle: 'rectRot',
      showLine: false,
      yAxisID: 'y',
      order: 99
    }] : [])
    ] : []),
    ...(sensor.type === '5' && showTemp2 ? [{
      label: 'Temperature 2 (°C)',
      data: readings ? smoothTemperatureData(
        readings
          .filter(reading => 
            typeof reading.temperature_2 === 'number' &&
            reading.temperature_2 !== null &&
            !isNaN(reading.temperature_2)
          )
          .map(reading => ({
            x: new Date(reading.timestamp),
            y: reading.temperature_2 as number
          }))
      ) : [],
      borderColor: 'rgb(255, 99, 132)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y'
    },
    ...(shouldOverlayLive && typeof liveSensor.temperature2 === 'number' && showTemp2 ? [{
      label: 'Live Temperature 2',
      data: [{ x: new Date(liveSensor.timestamp), y: liveSensor.temperature2 }],
      borderColor: 'rgba(255,99,132,0.5)',
      backgroundColor: 'rgba(255,99,132,1)',
      pointRadius: 6,
      pointStyle: 'triangle',
      showLine: false,
      yAxisID: 'y',
      order: 99
    }] : [])
    ] : []),
    ...(sensor.type === '3' ? [{
      label: 'Humidity (%RH)',
      data: readings?.filter(reading => 
        reading.temperature !== 8001 && reading.temperature !== 8002
      ).map(reading => ({
        x: new Date(reading.timestamp),
        y: reading.hum || 0
      })) || [],
      borderColor: 'rgb(192, 75, 192)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y1'
    }] : []),
    ...(showBattery ? [{
      label: 'Battery (V)',
      data: readings ? smoothBatteryData(
        readings
          .filter(reading => reading.battery >= 0.5)
          .map(reading => ({
            x: new Date(reading.timestamp),
            y: reading.battery
          }))
      ) : [],
      borderColor: 'rgb(255, 159, 64)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y2'
    },
    ...(shouldOverlayLive && typeof liveSensor.Battery === 'string' && showBattery ? [{
      label: 'Live Battery',
      data: [{ x: new Date(liveSensor.timestamp), y: parseFloat(liveSensor.Battery) }],
      borderColor: 'rgba(255,159,64,0.5)',
      backgroundColor: 'rgba(255,159,64,1)',
      pointRadius: 6,
      pointStyle: 'star',
      showLine: false,
      yAxisID: 'y2',
      order: 99
    }] : [])
    ] : []),
    highAlarmDataset as unknown as ChartDataset<'line', TimeSeriesData[]>,
    lowAlarmDataset as unknown as ChartDataset<'line', TimeSeriesData[]>,
  ], [readings, liveData, showTemp1, showTemp2, showBattery, shouldOverlayLive, liveSensor, sensor.type, highAlarmDataset, lowAlarmDataset, smoothTemperatureData, smoothBatteryData]);

  // Compute options for direct mutation
  const chartOptions = useMemo(() => {
    const baseOptions = getChartOptions({
      sensorName: sensor.name,
      selectedRangeHours: selectedRange.hours,
      onZoomChange: setIsZoomed,
      showHumidity: sensor.type === '3',
      showBattery
    });
    return {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        x: {
          ...baseOptions.scales?.x,
          min: xRange.min,
          max: xRange.max,
        }
      },
      plugins: {
        ...baseOptions.plugins,
        zoom: {
          ...baseOptions.plugins?.zoom,
          onZoom: ({chart}: any) => {
            const xScale = chart.scales['x'];
            setXRange({min: xScale.min, max: xScale.max});
          },
          onPan: ({chart}: any) => {
            const xScale = chart.scales['x'];
            setXRange({min: xScale.min, max: xScale.max});
          }
        },
        annotation: {
          annotations: {
            ...(alarmSetpoints.high && {
              highLine: {
                type: 'line' as const,
                yMin: alarmSetpoints.high,
                yMax: alarmSetpoints.high,
                borderColor: 'red',
                borderWidth: 2,
                label: {
                  content: 'High Alarm',
                  enabled: true,
                  position: 'end' as const
                }
              }
            }),
            ...(alarmSetpoints.low && {
              lowLine: {
                type: 'line' as const,
                yMin: alarmSetpoints.low,
                yMax: alarmSetpoints.low,
                borderColor: 'blue',
                borderWidth: 2,
                label: {
                  content: 'Low Alarm',
                  enabled: true,
                  position: 'end' as const
                }
              }
            })
          }
        }
      }
    };
  }, [sensor.name, selectedRange.hours, showBattery, alarmSetpoints, setIsZoomed, getChartOptions, xRange]);

  // Directly mutate chart instance on every data/options update
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data.datasets = datasets;
      chartRef.current.options = chartOptions;
      chartRef.current.update('none');
    }
  }, [datasets, chartOptions]);

  return (
    <div className="p-2 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {sensor.name}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Serial: {formatSensorSerial(sensor.serialNo, sensor.type)}
          </p>
        </div>

        <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            {timeRanges.map((range) => (
              <button
                key={range.label}
                onClick={() => handleRangeChange(range)}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm flex items-center ${
                  selectedRange === range
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isRangeChanging && selectedRange === range && (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                )}
                {range.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {sensor.type === '5' && (
              <>
                <label className="flex items-center text-xs sm:text-sm gap-1">
                  <input
                    type="checkbox"
                    checked={showTemp1}
                    onChange={() => setShowTemp1(v => !v)}
                    className="form-checkbox rounded text-indigo-600"
                  />
                  Channel 1
                </label>
                <label className="flex items-center text-xs sm:text-sm gap-1">
                  <input
                    type="checkbox"
                    checked={showTemp2}
                    onChange={() => setShowTemp2(v => !v)}
                    className="form-checkbox rounded text-pink-500"
                  />
                  Channel 2
                </label>
              </>
            )}
            {/* Only show Battery button if not type 4 or 5 */}
            {sensor.type !== '4' && sensor.type !== '5' && (
              <button
                onClick={() => setShowBattery(!showBattery)}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm flex items-center ${
                  showBattery
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Battery className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Battery
              </button>
            )}
            {isZoomed && (
              <button
                onClick={handleResetZoom}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 ${
                  isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Reset Zoom
              </button>
            )}
            {selectedRange.hours !== Infinity && (
              <>
                <button
                  onClick={() => moveTimeWindow('backward')}
                  disabled={isRangeChanging}
                  className={`p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Previous period"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => moveTimeWindow('forward')}
                  disabled={isRangeChanging || dateRange.end >= new Date()}
                  className={`p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Next period"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center text-xs sm:text-sm gap-1">
              <input
                type="checkbox"
                checked={showHighAlarms}
                onChange={() => setShowHighAlarms(v => !v)}
                className="form-checkbox rounded text-red-500"
              />
              High Alarms
            </label>
            <label className="flex items-center text-xs sm:text-sm gap-1">
              <input
                type="checkbox"
                checked={showLowAlarms}
                onChange={() => setShowLowAlarms(v => !v)}
                className="form-checkbox rounded text-blue-500"
              />
              Low Alarms
            </label>
          </div>
        </div>
        <div className="relative w-full h-[300px] md:h-[500px] -mx-3 sm:mx-0">
          {isLoading || isRangeChanging ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
                <span className="text-gray-500 dark:text-gray-400">
                  {isRangeChanging ? 'Updating data...' : 'Loading data...'}
                </span>
              </div>
            </div>
          ) : isChartReady && readings && readings.length > 0 ? (
            <Line
              key="sensor-details-chart"
              ref={chartRef}
              data={{ datasets: [] }}
              options={{}}
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <span className="text-gray-500 dark:text-gray-400">No data available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}